import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BusinessIcon from '@mui/icons-material/Business';
import ShareIcon from '@mui/icons-material/Share';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SyncIcon from '@mui/icons-material/Sync';
import { DataGrid, GridColDef, GridRenderCellParams, GridSelectionModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { IAdminWatchlist, useAdminUserWatchlistsQuery } from '../../api/admin';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';
import ShareWatchlistsDialog from './ShareWatchlistsDialog';

interface Props {
	userId: number;
	/** Fill the parent (a flex column) instead of using a fixed viewport-based height. */
	fill?: boolean;
}

export default function UserWatchlistsTable({ userId, fill }: Props) {
	const navigate = useNavigate();
	const { data: watchlists, isLoading } = useAdminUserWatchlistsQuery(userId);

	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
	const [shareDialogOpen, setShareDialogOpen] = useState(false);

	const selectedWatchlists: IAdminWatchlist[] = (watchlists ?? []).filter((wl) =>
		selectionModel.includes(wl.id)
	);

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: 'Watchlist Name',
			flex: 1,
			minWidth: 180,
		},
		{
			field: 'shared_from_email',
			headerName: 'Shared From',
			width: 220,
			renderCell: (params: GridRenderCellParams) => {
				if (!params.value) return null;
				return (
					<Stack direction="row" spacing={0.5} alignItems="center">
						{params.row.is_sync_share && (
							<Tooltip title="Auto-synced"><SyncIcon fontSize="small" color="primary" /></Tooltip>
						)}
						{params.row.admin_shared_by_email && (
							<Tooltip title={`Shared by admin: ${params.row.admin_shared_by_email}`}>
								<AdminPanelSettingsIcon fontSize="small" color="warning" />
							</Tooltip>
						)}
						<Typography variant="body2">{params.value}</Typography>
					</Stack>
				);
			},
		},
		{
			field: 'created_at',
			headerName: 'Created',
			width: 130,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{
			field: 'companies',
			headerName: 'Companies',
			width: 110,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams) => (
				<Tooltip title="View Companies">
					<IconButton
						size="small"
						onClick={() =>
							navigate(`/admin/watchlists/${params.row.id}/companies`, {
								state: { watchlistName: params.row.name, userId },
							})
						}
					>
						<BusinessIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			),
		},
	];

	return (
		<Box sx={fill ? { ...{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }, width: '100%' } : { height: 'calc(100vh - 310px)', width: '100%' }}>
			{selectionModel.length > 0 && (
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
					<Button
						variant="contained"
						size="small"
						startIcon={<ShareIcon />}
						onClick={() => setShareDialogOpen(true)}
					>
						Share {selectionModel.length} Watchlist{selectionModel.length !== 1 ? 's' : ''}
					</Button>
				</Box>
			)}
			<DataGrid
				sx={fill ? { flex: 1, minHeight: 0 } : undefined}
				rows={watchlists ?? []}
				columns={columns}
				loading={isLoading}
				density="compact"
				checkboxSelection
				disableSelectionOnClick
				selectionModel={selectionModel}
				onSelectionModelChange={setSelectionModel}
				components={{ Toolbar: AdminQuickFilterToolbar }}
				componentsProps={{ toolbar: { showQuickFilter: true } }}
			/>
			<ShareWatchlistsDialog
				open={shareDialogOpen}
				onClose={() => {
					setShareDialogOpen(false);
					setSelectionModel([]);
				}}
				watchlists={selectedWatchlists}
				sourceUserId={userId}
			/>
		</Box>
	);
}
